// @ts-nocheck
/**
 * Engagement Bar Chart Component
 * Carbon Charts implementation for engagement metrics visualization
 */

import React, { useMemo } from 'react';
import { StackedBarChart } from '@carbon/charts-react';
import { Tile, Loading, InlineNotification } from '@carbon/react';
import { ChartBar } from '@carbon/icons-react';
import '@carbon/charts-react/styles.css';

interface EngagementData {
  day: string;
  sessions: number;
  minutes: number;
  category: string;
}

interface EngagementBarChartProps {
  data: EngagementData[];
  height?: number;
  title?: string;
  loading?: boolean;
  error?: string;
}

const EngagementBarChart: React.FC<EngagementBarChartProps> = ({
  data,
  height = 300,
  title = 'Weekly Engagement Metrics',
  loading = false,
  error = '',
}) => {
  // Transform data for Carbon Charts format
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Group data by day and category
    const groupedData: { [key: string]: { [category: string]: number } } = {};

    data.forEach(item => {
      if (!groupedData[item.day]) {
        groupedData[item.day] = {};
      }
      groupedData[item.day][item.category] = item.minutes;
    });

    // Transform to Carbon Charts format
    const result: any[] = [];
    Object.keys(groupedData).forEach(day => {
      Object.keys(groupedData[day]).forEach(category => {
        result.push({
          group: day,
          key: category,
          value: groupedData[day][category],
        });
      });
    });

    return result;
  }, [data]);

  // Carbon Charts options with Sigma08 theme colors
  const chartOptions = useMemo(
    () => ({
      title: '',
      axes: {
        left: {
          title: 'Learning Minutes',
          mapsTo: 'value',
        },
        bottom: {
          title: 'Day',
          mapsTo: 'group',
          scaleType: 'labels' as const,
        },
      },
      height,
      theme: 'white',
      color: {
        scale: {
          'Active Learning': '#8a3ffc', // Galaxy Purple
          Practice: '#ffc700', // Saffron Orange
          Review: '#0043ce', // Carbon Blue
          default: '#525252', // Carbon Gray
        },
      },
      grid: {
        x: {
          enabled: false,
        },
        y: {
          enabled: true,
        },
      },
      bars: {
        maxWidth: 40,
      },
      tooltip: {
        enabled: true,
        customHTML: (datapoints: any[]) => {
          if (!datapoints || datapoints.length === 0) return '';

          // Group datapoints by day
          const dayData = datapoints[0].group;
          const sessionData = data.find(d => d.day === dayData);

          return `
          <div class="bx--cc--tooltip">
            <p class="bx--cc--tooltip-total">${dayData}</p>
            <ul class="bx--cc--tooltip-data">
              ${datapoints
                .map(
                  point => `
                <li>
                  <span class="bx--cc--tooltip-data__label">${point.key}:</span>
                  <span class="bx--cc--tooltip-data__value">${point.value} min</span>
                </li>
              `
                )
                .join('')}
              ${
                sessionData
                  ? `
                <li style="border-top: 1px solid var(--cds-border-subtle); padding-top: 4px; margin-top: 4px;">
                  <span class="bx--cc--tooltip-data__label">Total Sessions:</span>
                  <span class="bx--cc--tooltip-data__value">${sessionData.sessions}</span>
                </li>
              `
                  : ''
              }
            </ul>
          </div>
        `;
        },
      },
      legend: {
        enabled: true,
        position: 'top' as const,
        clickable: true,
      },
      animations: {
        enabled: true,
      },
    }),
    [height, data]
  );

  if (loading) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            gap: 'var(--cds-spacing-03)',
          }}
        >
          <Loading withOverlay={false} />
          <span style={{ color: 'var(--cds-text-secondary)' }}>Loading engagement data...</span>
        </div>
      </Tile>
    );
  }

  if (error) {
    return (
      <Tile>
        <InlineNotification title="Chart Error" subtitle={error} kind="error" lowContrast hideCloseButton />
      </Tile>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            color: 'var(--cds-text-secondary)',
          }}
        >
          <ChartBar size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
          <span>No engagement data available</span>
        </div>
      </Tile>
    );
  }

  return (
    <Tile>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--cds-spacing-03)',
          marginBottom: 'var(--cds-spacing-05)',
        }}
      >
        <ChartBar size={20} style={{ color: 'var(--cds-interactive-01)' }} />
        <h4
          style={{
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
            letterSpacing: 'var(--cds-productive-heading-03-letter-spacing)',
            margin: 0,
          }}
        >
          {title}
        </h4>
      </div>

      <div
        style={{
          backgroundColor: 'var(--cds-layer-01)',
          borderRadius: 'var(--cds-border-radius)',
          padding: 'var(--cds-spacing-03)',
        }}
      >
        <StackedBarChart data={chartData} options={chartOptions} />
      </div>
    </Tile>
  );
};

export default EngagementBarChart;
